import { ComponentProps, forwardRef, useId, useState } from "react";
import { TextInput } from "react-native";
import { Label, styled, Text, TextArea as TGTextArea, View } from "tamagui";

const getLableMaxWidth = (totalWidth: number, percent: number) => {
  return Math.round((totalWidth / 100) * percent);
};

const TextAreaLabelText = styled(Text, {
  name: "TextAreaLabelText",

  numberOfLines: 1,
  ellipsizeMode: "tail",

  color: "$gray-primary-900",
  variants: {
    font: {
      lg: {
        fontFamily: "$body",
        fontWeight: "$5",
        fontSize: "$4",
        lineHeight: "$4",
      },
      md: {
        fontFamily: "$body",
        fontWeight: "$5",
        fontSize: "$3",
        lineHeight: "$3",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

const TextAreaBase = styled(TGTextArea, {
  name: "TextAreaBase",
  variants: {
    varinat: {
      default: {
        borderColor: "$gray-secondary-400",
        borderWidth: "$0.2",
        focusStyle: {
          outlineWidth: 0,
        },
      },
    },
    font: {
      lg: {
        fontFamily: "$body",
        fontWeight: "$4",
        fontSize: "$3",
        lineHeight: "$3",
        color: "$gray-800",
      },
      md: {},
    },
    rounded: {
      lg: {
        borderRadius: "$5",
      },
    },
    size: {
      lg: {
        paddingHorizontal: "$2",
        paddingVertical: "$2",
      },
    },
  } as const,
  defaultVariants: {
    varinat: "default",
    font: "lg",
    rounded: "lg",
    size: "lg",
  },
});

const ErrorMessage = styled(Text, {
  name: "ErrorMessage",
  ellipsizeMode: "tail",
  numberOfLines: 1,
  position: "absolute",
  left: "$2",
  backgroundColor: "white",
  paddingHorizontal: 2,
  color: "$error-500",
  fontFamily: "$subtitle",
  fontSize: "$1",
  lineHeight: "$1",
  fontWeight: "$4",
  bottom: -5.4,
});

type TextAreaProps = ComponentProps<typeof TextAreaBase> & {
  label?: string;
  error?: string;
};

export const TextAreaWithLabel = forwardRef<TextInput | null, TextAreaProps>(
  ({ label, error, font, ...rest }, ref) => {
    const [width, setWidth] = useState(0);
    const id = useId();
    const textAreaId = "textarea-" + id;

    return (
      <View position="relative" data-id={"textarea-wrapper"}>
        <Label
          position="absolute"
          top={font === "md" ? -13 : -14}
          marginLeft={font === "md" ? "$3" : "$2"}
          padding={0}
          backgroundColor={"white"}
          htmlFor={textAreaId}
        >
          <TextAreaLabelText
            font={font}
            data-id={"textarea-label"}
            maxWidth={getLableMaxWidth(width, 90)}
          >
            {label}
          </TextAreaLabelText>
        </Label>
        <TextAreaBase
          font={font}
          data-id={"textarea"}
          onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
          ref={ref}
          id={textAreaId}
          {...rest}
        />
        {error && (
          <ErrorMessage maxWidth={getLableMaxWidth(width, 90)}>
            {error}
          </ErrorMessage>
        )}
      </View>
    );
  },
);
